import { motion } from "framer-motion";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";

const INVESTOR_POINTS = [
  "Verified investment-ready opportunities",
  "Direct access to Goa's premium business ecosystem",
  "Tourism, hospitality, infrastructure & lifestyle ventures",
  "One platform for expansion into Goa",
];

const BUSINESS_POINTS = [
  "FREE registration for Goa-based businesses",
  "Visibility to global investors & enterprises",
  "Expand internationally via strategic partnerships",
  "Premium exposure for hospitality, tourism & lifestyle",
  "Zero entry barriers",
];

const INVESTOR_IMG =
  "https://images.unsplash.com/photo-1731458769726-cef60c792665?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBnbG9iYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBsdXh1cnl8ZW58MHx8fHwxNzc5NDM2Nzc3fDA&ixlib=rb-4.1.0&q=85";

const MARINA_IMG =
  "https://images.pexels.com/photos/5231168/pexels-photo-5231168.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const SplitAudience = () => {
  return (
    <section className="relative bg-[#02060D]">
      {/* For Global Investors */}
      <div
        id="investors"
        data-testid="investors-section"
        className="relative py-24 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 lg:order-2 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={INVESTOR_IMG}
                alt="Global investors"
                className="w-full h-full object-cover grayscale contrast-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#02060D] via-[#02060D]/20 to-transparent" />
              <div className="absolute bottom-8 left-8 right-8">
                <div className="border-l-2 border-[#D1B677] pl-4">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#D1B677] font-medium mb-1">
                    For Global Investors
                  </div>
                  <div className="text-white font-serif text-2xl leading-tight">
                    Capital meets coastline.
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -top-8 -left-8 w-32 h-32 border border-[#D1B677]/30 -z-10 hidden lg:block" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-6 lg:order-1"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="text-xs tracking-[0.3em] uppercase text-[#D1B677] font-medium">
                For Global Investors & Enterprises
              </span>
            </div>
            <h2 className="font-serif text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] mb-8">
              Access high-growth{" "}
              <span className="italic text-[#D1B677]">opportunities across Goa.</span>
            </h2>
            <ul className="space-y-4 mb-10">
              {INVESTOR_POINTS.map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle
                    size={20}
                    weight="light"
                    className="text-[#D1B677] mt-0.5 shrink-0"
                  />
                  <span className="text-[#A1AEBF] text-base leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
            <a
              href="#register"
              data-testid="investors-cta-btn"
              className="group inline-flex items-center gap-3 px-7 py-4 border border-[#D1B677] text-[#D1B677] text-sm font-medium hover:bg-[#D1B677] hover:text-[#02060D] transition-all"
            >
              Explore Opportunities
              <ArrowRight
                size={18}
                weight="bold"
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
          </motion.div>
        </div>
      </div>

      {/* For Goa Businesses */}
      <div
        id="businesses"
        data-testid="businesses-section"
        className="relative py-24 md:py-32 bg-[#050B14] border-y border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={MARINA_IMG}
                alt="Goa marina"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#02060D] via-[#02060D]/30 to-[#02060D]/10" />
              <div className="absolute top-8 right-8 left-8 flex justify-end">
                <div className="border border-[#D1B677] bg-[#02060D]/80 backdrop-blur-md px-4 py-3">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#D1B677]">
                    FREE
                  </div>
                  <div className="text-white text-xs mt-0.5">Goa Business Onboarding</div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-8 -right-8 w-32 h-32 border border-[#0C5A48] -z-10 hidden lg:block" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="text-xs tracking-[0.3em] uppercase text-[#D1B677] font-medium">
                For Goa Businesses & Service Providers
              </span>
            </div>
            <h2 className="font-serif text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] mb-6">
              Take your Goa business to the{" "}
              <span className="italic text-[#D1B677]">global stage.</span>
            </h2>

            <div className="inline-flex items-center gap-3 border border-[#D1B677]/50 bg-[#D1B677]/[0.06] px-4 py-2 mb-8">
              <span className="w-1.5 h-1.5 bg-[#D1B677] rounded-full" />
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#D1B677] font-medium">
                100% Free · Goa-based businesses only
              </span>
            </div>

            <ul className="space-y-4 mb-10">
              {BUSINESS_POINTS.map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle
                    size={20}
                    weight="light"
                    className="text-[#D1B677] mt-0.5 shrink-0"
                  />
                  <span className="text-[#A1AEBF] text-base leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>

            <a
              href="#register"
              data-testid="businesses-cta-btn"
              className="group inline-flex items-center gap-3 px-7 py-4 bg-[#D1B677] text-[#02060D] text-sm font-semibold hover:bg-[#E6CC8A] transition-all"
            >
              Register Your Business Free
              <ArrowRight
                size={18}
                weight="bold"
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SplitAudience;
